import React, { useState } from "react";
import { MapPin, CheckCircle2, AlertCircle, ExternalLink, Pencil, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { errText, fmtDate } from "../../lib/useApi";

const MAPS_RE = /^https:\/\/((www\.|maps\.)?google\.[a-z.]{2,6}\/maps|maps\.app\.goo\.gl\/|goo\.gl\/maps\/|maps\.google\.[a-z.]{2,6}\/)/i;

export const LocationCard = ({ profile, onSaved }) => {
  const saved = profile?.map_url;
  const [editing, setEditing] = useState(!saved);
  const [url, setUrl] = useState(saved || "");
  const [pin, setPin] = useState(profile?.pin || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    const u = url.trim();
    if (!MAPS_RE.test(u)) { setErr("Paste a valid Google Maps link (google.com/maps or maps.app.goo.gl)."); return; }
    if (pin && !/^\d{6}$/.test(pin)) { setErr("PIN code must be 6 digits."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/profile/student/location", { url: u, pin });
      toast.success(data.has_coordinates ? "Location saved and verified" : "Location saved");
      setEditing(false); onSaved?.();
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  };

  const status = profile?.location_status;
  return (
    <div data-testid="location-card" className="card-surface p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-1"><MapPin size={15} className="text-blue-500"/><h3 className="font-display text-base" style={{ color: "var(--text)" }}>Share Your Google Maps Location</h3></div>
      <p className="text-xs mb-4" style={{ color: "var(--text-dim)" }}>Only the admin team sees this, to match you with nearby tasks. It is never shown publicly or to companies.</p>

      {saved && !editing && (
        <div data-testid="location-status" className="flex items-start gap-2 p-3 rounded-md border mb-4 text-xs" style={{ borderColor: "var(--border)" }}>
          {status === "verified"
            ? <CheckCircle2 size={14} className="text-emerald-500 shrink-0 mt-0.5"/>
            : <AlertCircle size={14} className="text-amber-500 shrink-0 mt-0.5"/>}
          <div style={{ color: "var(--text-muted)" }}>
            {status === "verified" ? "Location saved and verified" : "Link saved, but no exact point was found in it. Open the place in Google Maps, tap Share, and paste that link for distance matching."}
            <div className="mt-0.5" style={{ color: "var(--text-dim)" }}>Updated {fmtDate(profile?.location_updated_at)}{profile?.pin ? ` · PIN ${profile.pin}` : ""}</div>
          </div>
        </div>
      )}

      {editing && (
        <div className="space-y-3 mb-4">
          <input data-testid="location-url-input" value={url} onChange={e => setUrl(e.target.value)} placeholder="Paste your Google Maps location link here"
                 className="w-full text-sm px-3 py-3 rounded-md border outline-none bg-transparent" style={{ borderColor: "var(--border-strong)", color: "var(--text)" }}/>
          <input data-testid="location-pin-input" value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="PIN code (optional)" inputMode="numeric"
                 className="w-full sm:w-48 text-sm px-3 py-3 rounded-md border outline-none bg-transparent" style={{ borderColor: "var(--border-strong)", color: "var(--text)" }}/>
          {err && <div data-testid="location-error" className="text-xs text-rose-500">{err}</div>}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {editing ? (
          <button data-testid="location-save-btn" onClick={save} disabled={busy} className="btn-primary px-4 py-2.5 rounded-full text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>} Save Location
          </button>
        ) : (
          <button data-testid="location-edit-btn" onClick={() => setEditing(true)} className="btn-ghost px-4 py-2.5 rounded-full text-xs inline-flex items-center gap-1.5"><Pencil size={13}/> Edit Location</button>
        )}
        {saved && (
          <a data-testid="location-open-map-btn" href={saved} target="_blank" rel="noopener noreferrer" className="btn-ghost px-4 py-2.5 rounded-full text-xs inline-flex items-center gap-1.5"><ExternalLink size={13}/> Open Map</a>
        )}
        {editing && saved && (
          <button data-testid="location-cancel-btn" onClick={() => { setEditing(false); setUrl(saved); setErr(""); }} className="px-3 py-2.5 text-xs" style={{ color: "var(--text-dim)" }}>Cancel</button>
        )}
      </div>
    </div>
  );
};

export default LocationCard;
